import ApiBase from '../apiBase';

export type InspectionReportStatus = 'Draft' | 'Completed' | 'Archived';

export type TrendKind = 'same' | 'improved' | 'regressed' | 'changed';

export interface FileAttachmentMini {
    id: string;
    fileName?: string;
}

export interface InspectionReportAnswerDto {
    id: string;
    questionId: string;
    questionText: string;
    questionCategoryId?: string | null;
    questionCategoryName?: string | null;
    categoryOrder?: number | null;
    yesNo?: boolean | null;
    description?: string | null;
    findings?: string | null;
    proposedSolutions?: string | null;
    fileAttachments: FileAttachmentMini[];
}

export interface InspectionReportRiskAnswerDto {
    questionCategoryId: string;
    questionCategoryName: string;
    probabilityLevel: number;
    impactLevel: number;
    threatLevel: number;
    appliedMeasureCoefficient: number;
    residualRiskLevel: number;
    isAfterMeasures: boolean;
}

export interface InspectionReportDetailDto {
    id: string;
    pinId: string;
    authorUserId: string;
    status: InspectionReportStatus;
    startedAt: string;
    completedAt?: string | null;
    title?: string | null;
    executiveSummary?: string | null;
    pdfFileAttachmentId?: string | null;
    answers: InspectionReportAnswerDto[];
    riskAnswers: InspectionReportRiskAnswerDto[];
}

export interface InspectionReportListItemDto {
    id: string;
    pinId: string;
    status: InspectionReportStatus;
    startedAt: string;
    completedAt?: string | null;
    title?: string | null;
    pdfFileAttachmentId?: string | null;
    answeredCount: number;
    findingCount: number;
}

export interface CreatedInspectionReportResponse {
    id: string;
    pinId: string;
    status: InspectionReportStatus;
    startedAt: string;
    title?: string | null;
}

export interface ImportedInspectionReportResponse {
    id: string;
    pinId: string;
    status: InspectionReportStatus;
    startedAt: string;
    completedAt?: string | null;
    title?: string | null;
    importedAnswerCount: number;
    importedRiskAnswerCount: number;
}

export interface ComparisonCell {
    reportId: string;
    hasAnswer: boolean;
    yesNo?: boolean | null;
    findings?: string | null;
    proposedSolutions?: string | null;
    description?: string | null;
}

export interface ComparisonRow {
    questionId: string;
    questionText: string;
    categoryName: string;
    categoryOrder: number;
    trend: TrendKind;
    cells: ComparisonCell[];
}

export interface RiskMeasureSnapshotDto {
    probabilityLevel: number;
    impactLevel: number;
    threatLevel: number;
    appliedMeasureCoefficient: number;
    residualRiskLevel: number;
}

export interface RiskComparisonCell {
    reportId: string;
    beforeMeasures?: RiskMeasureSnapshotDto | null;
    afterMeasures?: RiskMeasureSnapshotDto | null;
}

export interface RiskComparisonRow {
    questionCategoryId: string;
    categoryName: string;
    categoryOrder: number;
    cells: RiskComparisonCell[];
}

export interface ReportColumnDto {
    id: string;
    title?: string | null;
    startedAt: string;
    completedAt?: string | null;
    createdDate?: string | null;
    status: InspectionReportStatus;
    pdfFileAttachmentId?: string | null;
    authorUserId?: string;
    authorFirstName?: string | null;
    authorLastName?: string | null;
}

export interface ComparisonSummary {
    totalQuestions: number;
    improved: number;
    regressed: number;
    unchanged: number;
    changed: number;
}

export interface CompareInspectionReportsResponse {
    reports: ReportColumnDto[];
    rows: ComparisonRow[];
    riskRows: RiskComparisonRow[];
    summary: ComparisonSummary;
}

export type UpsertAnswerPayload = Partial<{
    yesNo: boolean | null;
    description: string | null;
    findings: string | null;
    proposedSolutions: string | null;
}>;

const API_BASE = '/api/InspectionReports';

function normalizeRiskSnapshot(raw: unknown): RiskMeasureSnapshotDto | null {
    if (raw == null || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    return {
        probabilityLevel: Number(o.probabilityLevel ?? o.ProbabilityLevel ?? 0),
        impactLevel: Number(o.impactLevel ?? o.ImpactLevel ?? 0),
        threatLevel: Number(o.threatLevel ?? o.ThreatLevel ?? 0),
        appliedMeasureCoefficient: Number(o.appliedMeasureCoefficient ?? o.AppliedMeasureCoefficient ?? 0),
        residualRiskLevel: Number(o.residualRiskLevel ?? o.ResidualRiskLevel ?? 0),
    };
}

function normalizeRiskComparisonCell(raw: unknown): RiskComparisonCell {
    if (raw == null || typeof raw !== 'object') {
        return { reportId: '', beforeMeasures: null, afterMeasures: null };
    }
    const o = raw as Record<string, unknown>;
    return {
        reportId: String(o.reportId ?? o.ReportId ?? ''),
        beforeMeasures: normalizeRiskSnapshot(o.beforeMeasures ?? o.BeforeMeasures),
        afterMeasures: normalizeRiskSnapshot(o.afterMeasures ?? o.AfterMeasures),
    };
}

function normalizeRiskComparisonRow(raw: unknown): RiskComparisonRow {
    if (raw == null || typeof raw !== 'object') {
        return { questionCategoryId: '', categoryName: '', categoryOrder: 0, cells: [] };
    }
    const o = raw as Record<string, unknown>;
    const cellsRaw = o.cells ?? o.Cells;
    const cells = Array.isArray(cellsRaw) ? cellsRaw.map(normalizeRiskComparisonCell) : [];
    return {
        questionCategoryId: String(o.questionCategoryId ?? o.QuestionCategoryId ?? ''),
        categoryName: String(o.categoryName ?? o.CategoryName ?? ''),
        categoryOrder: Number(o.categoryOrder ?? o.CategoryOrder ?? 0),
        cells,
    };
}

function normalizeCompareResponse(raw: unknown): CompareInspectionReportsResponse {
    if (raw == null || typeof raw !== 'object') {
        return {
            reports: [],
            rows: [],
            riskRows: [],
            summary: { totalQuestions: 0, improved: 0, regressed: 0, unchanged: 0, changed: 0 },
        };
    }
    const o = raw as Record<string, unknown>;
    const riskRaw = o.riskRows ?? o.RiskRows;
    const riskRows = Array.isArray(riskRaw) ? riskRaw.map(normalizeRiskComparisonRow) : [];
    const summary = (o.summary ?? o.Summary) as Record<string, unknown> | undefined;
    return {
        reports: (o.reports ?? o.Reports ?? []) as ReportColumnDto[],
        rows: (o.rows ?? o.Rows ?? []) as ComparisonRow[],
        riskRows,
        summary: {
            totalQuestions: Number(summary?.totalQuestions ?? summary?.TotalQuestions ?? 0),
            improved: Number(summary?.improved ?? summary?.Improved ?? 0),
            regressed: Number(summary?.regressed ?? summary?.Regressed ?? 0),
            unchanged: Number(summary?.unchanged ?? summary?.Unchanged ?? 0),
            changed: Number(summary?.changed ?? summary?.Changed ?? 0),
        },
    };
}

export const inspectionReportsApi = {
    create(payload: { pinId: string; authorUserId: string; title?: string }) {
        return ApiBase.postJsonPromise<CreatedInspectionReportResponse>(API_BASE, payload);
    },

    importFromPin(payload: { pinId: string; authorUserId: string; title?: string }) {
        return ApiBase.postJsonPromise<ImportedInspectionReportResponse>(
            `${API_BASE}/import-from-pin`,
            payload
        );
    },

    getById(reportId: string): Promise<InspectionReportDetailDto> {
        return ApiBase.getPromise<InspectionReportDetailDto>(`${API_BASE}/${reportId}`) as Promise<InspectionReportDetailDto>;
    },

    /** Kaydedilen PDF (FileAttachment) Id — Complete sırasında PdfFileAttachmentId bağlandıysa raporu döner. */
    getByPdfFileAttachmentId(fileAttachmentId: string): Promise<InspectionReportDetailDto> {
        return ApiBase.getPromise<InspectionReportDetailDto>(
            `${API_BASE}/by-pdf-file/${fileAttachmentId}`
        ) as Promise<InspectionReportDetailDto>;
    },

    listByPin(pinId: string): Promise<InspectionReportListItemDto[]> {
        return ApiBase.getPromise<InspectionReportListItemDto[]>(`${API_BASE}?pinId=${pinId}`) as Promise<InspectionReportListItemDto[]>;
    },

    upsertAnswer(
        reportId: string,
        questionId: string,
        payload: UpsertAnswerPayload
    ): Promise<InspectionReportAnswerDto> {
        return ApiBase.putCategoryRiskAnswerPromise(
            `${API_BASE}/${reportId}/answers/${questionId}`,
            payload
        ) as Promise<InspectionReportAnswerDto>;
    },

    complete(reportId: string, payload: { pdfFileAttachmentId?: string; executiveSummary?: string }) {
        return ApiBase.postJsonPromise(`${API_BASE}/${reportId}/complete`, payload ?? {});
    },

    reopen(reportId: string) {
        return ApiBase.postJsonPromise(`${API_BASE}/${reportId}/reopen`, {});
    },

    compare(reportIds: string[]): Promise<CompareInspectionReportsResponse> {
        return ApiBase.getPromise<unknown>(`${API_BASE}/compare?ids=${reportIds.join(',')}`).then(
            normalizeCompareResponse
        );
    },
};

export default inspectionReportsApi;
